/**
 * Thin fetch wrapper that:
 *  - Prepends VITE_API_URL (or '/api' when proxied through nginx).
 *  - Always sends cookies (HttpOnly session cookie + readable CSRF cookie).
 *  - Echoes the CSRF cookie back as X-CSRF-Token on mutating verbs.
 *  - Detects FormData bodies and lets the browser set the multipart boundary.
 *  - Throws ApiError with parsed JSON body when response is not ok.
 */

const BASE = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function readCsrfFromCookie(): string | null {
  const match = document.cookie.match(/(?:^|; )datacole_csrf=([^;]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : null;
}

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 20_000;

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers = new Headers(opts.headers);
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body !== undefined && !isFormData) headers.set('content-type', 'application/json');

  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCsrfFromCookie();
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  // Abort after timeoutMs so a hung proxy/network never freezes the SPA's
  // bootstrap query in "loading" forever.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...opts,
      method,
      credentials: 'include',
      headers,
      signal: controller.signal,
      body:
        opts.body === undefined
          ? undefined
          : isFormData
            ? (opts.body as FormData)
            : JSON.stringify(opts.body),
    });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') {
      throw new ApiError(0, 'timeout', null);
    }
    throw new ApiError(0, (err as Error).message || 'network_error', null);
  } finally {
    clearTimeout(timer);
  }

  let body: unknown = null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else if (res.status !== 204) {
    body = await res.text().catch(() => null);
  }

  if (!res.ok) {
    let message: string = res.statusText || 'request_failed';
    if (body && typeof body === 'object' && 'error' in body) {
      message = String((body as { error: unknown }).error);
    }
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}
